import { MainLayout } from '../../Layout/MainLayout';
import { Button, FormContentIsland, Grid, HookForm, Stack, TextField } from '@razz-team/ui-web';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import {
  useAccountCreateMutation,
  useAccountProductCryptoQuery,
  useAccountProductFiatQuery,
} from '../../../api/accounts';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { useFormError } from '../../../hooks/useFormError';
import { AccountHolderField } from '../../Field/AccountHolderField';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { CurrencyField } from '../../Field/CurrencyField';
import { useBusinessQuery } from '../../../api/businesses';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';

interface CreateAccountFormValues {
  name: string;
  holderId: string;
  currency: string;
}

export function CreateAccountPage() {
  const { t } = useTranslation();
  const idempotencyKey = useIdempotencyKey();
  const accountProductFiatQuery = useAccountProductFiatQuery();
  const accountProductCryptoQuery = useAccountProductCryptoQuery();
  const businessQuery = useBusinessQuery();
  const businessCustomerId = businessQuery.data?.profile.id;
  const createAccountMutation = useAccountCreateMutation({ idempotencyKey });
  const navigate = useNavigate();

  const fiatConditions = accountProductFiatQuery.data?.product?.conditions;
  const cryptoConditions = accountProductCryptoQuery.data?.product?.conditions;
  const availableCurrencies = useMemo(() => {
    if (!fiatConditions || !cryptoConditions) return [];
    const fiatCurrencies = fiatConditions.flatMap((condition) =>
      condition.totalLimit - condition.usage.number > 0 ? condition.currency : [],
    );
    const cryptoCurrencies = cryptoConditions.flatMap((condition) =>
      condition.totalLimit - condition.usage.number > 0 ? condition.currency : [],
    );
    return [...fiatCurrencies, ...cryptoCurrencies];
  }, [fiatConditions, cryptoConditions]);

  const form = useForm<CreateAccountFormValues>({
    defaultValues: {
      holderId: businessQuery.data?.profile.id,
      currency: availableCurrencies[0],
    },
  });

  const [currency] = form.watch(['currency']);

  useFormError(form, 'currency', createAccountMutation.error);

  // Set default holderId to current business
  useEffect(() => {
    const currentHolderId = form.getValues('holderId');
    if (!currentHolderId && businessCustomerId) {
      form.setValue('holderId', businessCustomerId, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [businessCustomerId, form]);

  // Set default currency to first available
  useEffect(() => {
    const currentCurrency = form.getValues('currency');
    if (!currentCurrency && availableCurrencies.length > 0) {
      form.setValue('currency', availableCurrencies[0], {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [availableCurrencies, form]);

  const handleSubmit = useCallback(
    (values: CreateAccountFormValues) => {
      createAccountMutation.mutate({
        type: 'CURRENT',
        name: values.name || null,
        currency: values.currency,
        // if the selected holder is the current business, send null to use the business as account holder
        holderId: values.holderId === businessCustomerId ? null : values.holderId,
      });
    },
    [createAccountMutation, businessCustomerId],
  );

  useEffect(() => {
    if (createAccountMutation.data) {
      navigate({
        to: '/account/$accountId',
        params: { accountId: createAccountMutation.data.id },
      });
    }
  }, [createAccountMutation.data, navigate]);

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Open account')}
          parent={{
            to: '/account/accounts',
            title: t('Accounts'),
          }}
        />
      }
      compact
    >
      <Grid container component={HookForm} form={form} onSubmit={handleSubmit} size={12}>
        <Stack spacing={4} width="100%">
          <FormContentIsland title={t('Account details')}>
            <CurrencyField
              name="currency"
              label={t('Currency')}
              required
              options={availableCurrencies}
            />
            <AccountHolderField name="holderId" label={t('Account holder')} />
            <TextField
              name="name"
              label={t('Account name (optional)')}
              placeholder={t('Main {{currency}}', { currency })}
              fullWidth
            />
          </FormContentIsland>

          <Button
            type="submit"
            size="large"
            color="primary"
            loading={createAccountMutation.isPending}
            fullWidth
          >
            {t('Open account')}
          </Button>
        </Stack>
      </Grid>
    </MainLayout>
  );
}
